import { useMemo, useState } from 'react';
import { getProductPricing } from '../../../api/ordersApi.js';
import { classifyPortalError } from '../utils/apiError.js';
import { showErrorToast } from '../utils/toast.js';
import Dropdown from './Dropdown.jsx';
import Modal from './Modal.jsx';
import './AddProductModal.css';

function getDescription(product) {
  return product?.productDescription || product?.description || '';
}

export default function AddProductModal({ customerId, catalog, catalogLoading = false, onLoadCatalog, editingProduct, onCancel, onSave }) {
  const isEditing = Boolean(editingProduct);
  const [productCode, setProductCode] = useState(editingProduct?.productCode || '');
  const [qty, setQty] = useState(editingProduct ? String(editingProduct.quantity ?? '') : '');
  const [unitPrice, setUnitPrice] = useState(editingProduct ? String(editingProduct.unitPrice ?? '') : '');
  const [comments, setComments] = useState(editingProduct?.comments || '');
  const [rrp, setRrp] = useState(editingProduct?.rrp || 0);
  const [loading, setLoading] = useState(false);
  const [qtyError, setQtyError] = useState('');
  const [pricingNote, setPricingNote] = useState(null);

  const selectedProduct = useMemo(
    () => catalog.find((p) => p.productCode === productCode) ?? null,
    [catalog, productCode]
  );

  const total = (Number(qty) || 0) * (Number(unitPrice) || 0);

  const handleSelectProduct = async (code) => {
    setProductCode(code);
    setUnitPrice('');
    setComments('');
    setQty('');
    setQtyError('');
    if (!code) return;

    setLoading(true);
    try {
      const pricing = await getProductPricing(code, customerId);
      // Kept at full precision from the backend — the input no longer
      // forces a step="0.01" quantization, so there's nothing to round for.
      setUnitPrice(pricing.customerPrice != null ? String(pricing.customerPrice) : '');
      setRrp(Number(pricing.rrp) || 0);

      const reminderText = String(pricing.reminder ?? pricing.remainder ?? '').trim();
      const commentText = String(pricing.comments ?? '').trim();
      if (reminderText || commentText) {
        setPricingNote({ reminder: reminderText, comment: commentText });
      }
    } catch (err) {
      showErrorToast(classifyPortalError(err).message || 'Could not load pricing for this product.');
      onCancel();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productCode) return;
    const qtyNum = Number(qty);
    if (!qty || Number.isNaN(qtyNum) || qtyNum <= 0) {
      setQtyError('Quantity is required');
      return;
    }
    // The catalog only loads once the product dropdown is opened, so when
    // editing an existing line without touching the dropdown, `selectedProduct`
    // won't resolve even though `productCode` itself is already valid — fall
    // back to the line's own known description in that case.
    onSave({
      productCode,
      description: selectedProduct ? getDescription(selectedProduct) : (editingProduct?.description || ''),
      quantity: qtyNum,
      unitPrice: Number(unitPrice) || 0,
      totalPrice: total,
      comments,
      rrp,
    });
  };

  return (
    <>
      <Modal
        open
        title={isEditing ? 'Edit Product' : 'Add Product'}
        subtitle={isEditing ? 'Update this product line' : 'Add a new product to your order'}
        onClose={onCancel}
      >
        <form onSubmit={handleSubmit} noValidate>
          <div className="customer-portal-modal-card__body">
            <label className="customer-portal-form-field">
              <span className="customer-portal-form-field__label">Select Product</span>
              <Dropdown
                value={productCode}
                onChange={handleSelectProduct}
                placeholder="Select Product"
                noDataText="No products found"
                options={catalog.map((p) => ({
                  value: p.productCode,
                  title: p.productCode,
                  subtext: getDescription(p) || '—',
                }))}
                searchable
                searchPlaceholder="Search products…"
                showValueTooltip
                onOpen={onLoadCatalog}
                loading={catalogLoading}
                loadingText="Loading products…"
              />
            </label>

            <label className="customer-portal-form-field">
              <span className="customer-portal-form-field__label">Quantity</span>
              <input
                type="number"
                min="1"
                className={`customer-portal-input--no-spinner${qtyError ? ' is-error' : ''}`}
                placeholder="Enter quantity"
                value={qty}
                onChange={(e) => {
                  setQty(e.target.value);
                  if (qtyError) setQtyError('');
                }}
                onWheel={(e) => e.target.blur()}
              />
              {qtyError && (
                <div className="customer-portal-field-error">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="13" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {qtyError}
                </div>
              )}
            </label>

            <label className="customer-portal-form-field">
              <span className="customer-portal-form-field__label">Customer Price</span>
              <input
                type="text"
                placeholder={loading ? 'Loading…' : ''}
                value={unitPrice}
                readOnly
                disabled
              />
            </label>

            <label className="customer-portal-form-field">
              <span className="customer-portal-form-field__label">Total Price</span>
              <input type="text" value={total.toFixed(2)} readOnly disabled />
            </label>

            <label className="customer-portal-form-field customer-portal-form-field--wide">
              <span className="customer-portal-form-field__label">Product Comments</span>
              <input
                type="text"
                placeholder="Enter product comments (optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </label>
          </div>

          <div className="customer-portal-modal-card__footer">
            <button type="button" className="customer-portal-btn customer-portal-btn--ghost" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="customer-portal-btn customer-portal-btn--primary" disabled={!productCode || loading}>
              {isEditing ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </form>
      </Modal>

      {pricingNote && (
        <Modal
          open
          title="Before you continue"
          subtitle={selectedProduct?.productCode}
          onClose={() => setPricingNote(null)}
          className="customer-portal-product-note-modal"
          footer={
            <button type="button" className="customer-portal-btn customer-portal-btn--primary" onClick={() => setPricingNote(null)}>
              Got it
            </button>
          }
        >
          <div className="customer-portal-product-note-body">
            {pricingNote.reminder && (
              <div className="customer-portal-product-note-card customer-portal-product-note-card--reminder">
                <div className="customer-portal-product-note-card__label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  Reminder
                </div>
                <p className="customer-portal-product-note-card__text">{pricingNote.reminder}</p>
              </div>
            )}
            {pricingNote.comment && (
              <div className="customer-portal-product-note-card customer-portal-product-note-card--comment">
                <div className="customer-portal-product-note-card__label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Comment
                </div>
                <p className="customer-portal-product-note-card__text">{pricingNote.comment}</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
